import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { EnvService } from "./env.service";

export type AiFeatureAvailability = "ACTIVE" | "DISABLED" | "MAINTENANCE";

export interface AiPublicFeature {
  enabled: boolean;
  availability: AiFeatureAvailability;
  configuredEnabled: boolean;
  authRequired: boolean;
  disabledUntil: string | null;
}

export interface AiPublicFeatures {
  serverTime: string;
  features: {
    chat: AiPublicFeature;
    ste: AiPublicFeature;
  };
}

const FALLBACK_FEATURE: AiPublicFeature = {
  enabled: false,
  availability: "DISABLED",
  configuredEnabled: false,
  authRequired: true,
  disabledUntil: null,
};

/**
 * Caches GET /public/ai/features (anonymous, no AI role needed) so consumers can
 * gate chat/STE entry points on the effective availability flags instead of an AI role.
 */
@Injectable({
  providedIn: "root",
})
export class AiFeatureGateService {
  private readonly featuresSubject = new BehaviorSubject<AiPublicFeatures | null>(null);
  readonly features$ = this.featuresSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly env: EnvService,
  ) {}

  load(): Observable<AiPublicFeatures | null> {
    return this.http.get<AiPublicFeatures>(`${this.env.chatbotBackendUrl}/public/ai/features`).pipe(
      tap((features) => this.featuresSubject.next(features)),
      catchError(() => {
        this.featuresSubject.next(null);
        return of(null);
      }),
    );
  }

  get chatFeature(): AiPublicFeature {
    return this.featuresSubject.value?.features.chat ?? FALLBACK_FEATURE;
  }

  get steFeature(): AiPublicFeature {
    return this.featuresSubject.value?.features.ste ?? FALLBACK_FEATURE;
  }
}
